// base
import { type RefObject, useMemo, useRef } from 'react';

// hooks
import { useInfiniteScroll, type UseInfiniteScrollProps } from './useInfiniteScroll';

// models
import type { ResultError, BaseApiResponse } from 'modules';

// libraries
import {
  type InfiniteData,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult
} from '@tanstack/react-query';
import { uniqueId } from 'lodash-es';

export type UseCommonInfiniteQueryWithScrollOptionsType<TResData, TError> = Omit<
  UseInfiniteQueryOptions<TResData, TError, InfiniteData<TResData>, TResData, QueryKey, number>,
  | 'queryKey'
  | 'queryFn'
  | 'nextPageParam'
  | 'previousPageParam'
  | 'initialPageParam'
  | 'getNextPageParam'
  | 'getPreviousPageParam'
> & {
  infiniteScrollOptions?: UseInfiniteScrollProps;
};

/**
 * `useCommonInfiniteQueryWithScroll` 훅의 매개변수를 정의하는 인터페이스입니다.
 *
 * @template TReqData - 요청 데이터의 타입
 * @template TResData - 응답 데이터의 타입 (BaseApiResponse을 확장해야 함)
 * @template TError - 에러의 타입
 */
export interface UseCommonInfiniteQueryWithScrollParams<
  TReqData,
  TResData extends BaseApiResponse,
  TError extends ResultError = ResultError
> {
  /**
   * 요청에 사용할 데이터입니다.
   * `size`는 선택적으로 포함될 수 있습니다.
   */
  data: TReqData & { size?: number };

  /**
   * `useInfiniteQuery`에 전달할 추가 옵션입니다.
   * `queryKey`와 `queryFn`은 이 훅에서 처리하므로 제외됩니다.
   */
  options?: UseCommonInfiniteQueryWithScrollOptionsType<TResData, TError> | null;

  /**
   * 쿼리 키를 생성하는 함수입니다.
   *
   * @param data - 요청 데이터
   * @returns 쿼리 키 배열
   */
  queryKeyFactory: (
    data: TReqData
  ) => unknown[] | readonly unknown[] | { queryKey: unknown[] | readonly unknown[] };

  /**
   * 데이터를 가져오는 쿼리 함수입니다.
   *
   * @param data - 요청 데이터
   * @param page - 페이지 정보 (`page`와 `size`)
   * @returns 응답 데이터를 반환하는 Promise
   */
  queryFn: (
    data: TReqData,
    page: {
      page: number;
      size: number;
    }
  ) => Promise<TResData>;
}

/**
 * `useCommonInfiniteQueryWithScroll` 훅의 반환 타입입니다.
 *
 * @template TData - 데이터의 타입
 * @template TError - 에러의 타입
 */
export type UseCommonInfiniteQueryWithScrollResult<
  TData extends InfiniteData<BaseApiResponse>,
  TError extends ResultError = ResultError
> = UseInfiniteQueryResult<TData, TError> & {
  /** 마지막 페이지의 데이터 객체 */
  dataObject: TData['pages'][number]['resultObject'];
  /** 마지막 페이지의 데이터 배열 */
  dataSource: TData['pages'][number]['resultList'];
  /** 데이터의 총 개수 */
  totalElements: number;
  /** 데이터 페이지 총 개수 */
  totalPages: number;
  /** 무한 스크롤을 위한 마지막 요소의 Ref 객체 */
  lastElementRef: RefObject<HTMLDivElement>;
};

/**
 * 무한 스크롤을 지원하는 공통 쿼리 훅입니다.
 *
 * @template TReqData - 요청 데이터의 타입
 * @template TResData - 응답 데이터의 타입 (BaseApiResponse을 확장해야 함)
 * @template TError - 에러의 타입 (기본값은 ResultError)
 *
 * @param props - 훅에 필요한 매개변수들
 * @returns `useInfiniteQuery`의 결과와 `lastElementRef`를 포함한 객체를 반환합니다.
 *
 * @example
 * const {
 *   data,
 *   fetchNextPage,
 *   hasNextPage,
 *   isFetchingNextPage,
 *   lastElementRef,
 * } = useCommonInfiniteQueryWithScroll({
 *   data: requestData,
 *   queryKeyFactory: (data) => ['myQueryKey', data],
 *   queryFn: fetchFunction,
 * });
 */
export const useCommonInfiniteQueryWithScroll = <
  TReqData,
  TResData extends BaseApiResponse,
  TError extends ResultError = ResultError
>(
  props: UseCommonInfiniteQueryWithScrollParams<TReqData, TResData, TError>
): UseCommonInfiniteQueryWithScrollResult<InfiniteData<TResData>> => {
  const { data, options = null, queryKeyFactory, queryFn } = props;

  /** 무한 스크롤을 위한 마지막 요소의 Ref */
  const lastElementRef = useRef<HTMLDivElement | null>(null);

  const optionsRef = useRef<{
    infiniteScrollOptions: UseInfiniteScrollProps | null;
    queryOptions: UseCommonInfiniteQueryWithScrollOptionsType<TResData, TError> | null;
  }>({
    infiniteScrollOptions: null,
    queryOptions: null
  });

  useMemo(() => {
    if (!options) {
      optionsRef.current = {
        infiniteScrollOptions: null,
        queryOptions: null
      };
      return;
    }

    if (options.infiniteScrollOptions) {
      const { infiniteScrollOptions, ...rest } = options;
      optionsRef.current = {
        infiniteScrollOptions,
        queryOptions: rest
      };
      return;
    }

    optionsRef.current = {
      infiniteScrollOptions: null,
      queryOptions: options
    };
  }, [options]);

  /** 쿼리 키를 생성합니다. */
  const queryKey = useMemo(() => {
    const result = queryKeyFactory(data);

    // result가 객체인 경우 queryKey 사용
    if (result && typeof result === 'object' && 'queryKey' in result) {
      return [...result.queryKey];
    }

    // result가 배열인 경우 직접 사용
    if (Array.isArray(result)) {
      return [...result];
    }

    // fallback - 기본 쿼리 키 구조
    return ['default'];
  }, [data, queryKeyFactory]);

  const optionObj = useMemo(() => {
    /** 기본 옵션을 정의합니다. */
    const defaultOptions = {
      /**
       * 다음 페이지의 페이지 번호를 결정하는 함수입니다.
       *
       * @param lastPage - 마지막으로 가져온 페이지의 데이터
       * @param allPages - 지금까지 가져온 모든 페이지의 데이터 배열
       * @returns 다음 페이지의 번호 또는 undefined
       */
      getNextPageParam: (lastPage: TResData, allPages: TResData[]) => {
        const currentPage = allPages.length;
        const totalPageCount = lastPage.totalMap.totalPages || 0;

        return currentPage < totalPageCount ? currentPage : undefined;
      },

      /**
       * 이전 페이지의 페이지 번호를 결정하는 함수입니다.
       *
       * @param lastPage - 마지막으로 가져온 페이지의 데이터
       * @param allPages - 지금까지 가져온 모든 페이지의 데이터 배열
       * @returns 이전 페이지의 번호 또는 undefined
       */
      getPreviousPageParam: (_: TResData, allPages: TResData[]) => {
        const currentPage = allPages.length;

        return currentPage > 1 ? currentPage - 2 : undefined;
      },

      /** 초기 페이지 파라미터 설정 */
      initialPageParam: 0
    };

    if (!optionsRef.current.queryOptions) return { ...defaultOptions };

    return {
      ...defaultOptions,
      ...optionsRef.current.queryOptions
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsRef.current]);

  /** 무한 쿼리를 실행합니다. */
  const queryResult = useInfiniteQuery<TResData, TError, InfiniteData<TResData>, QueryKey, number>({
    queryKey,
    queryFn: ({ pageParam = 0 }) => queryFn(data, { page: pageParam, size: data.size || 10 }),
    ...optionObj
  });

  /** 데이터의 총 개수를 계산합니다. */
  const totalEl = useMemo(() => {
    if (!queryResult.data || !queryResult.data.pages) {
      return {
        totalElements: 0,
        totalPages: 0
      };
    }

    const datas = queryResult.data.pages;

    return {
      totalElements: queryResult.data.pages[datas.length - 1].totalMap.totalElements || 0,
      totalPages: queryResult.data.pages[datas.length - 1].totalMap.totalPages || 0
    };
  }, [queryResult.data]);

  /** 마지막 페이지의 데이터 객체를 반환합니다. */
  const dataObject = useMemo(() => {
    if (!queryResult.data?.pages) return null;

    const datas = queryResult.data.pages;

    return datas[datas.length - 1]?.resultObject ?? null;
  }, [queryResult.data]);

  /** 마지막 페이지의 데이터 배열을 반환합니다. */
  const dataSource = useMemo(() => {
    if (!queryResult.data?.pages) return [] as TResData['resultList'];

    const datas = queryResult.data.pages;

    return datas.flatMap((item) => item.resultList);
  }, [queryResult.data]);

  /** 무한 스크롤 훅을 사용하여 스크롤 이벤트를 처리합니다. */
  useInfiniteScroll({
    target: lastElementRef,
    onIntersect: queryResult.fetchNextPage,
    enabled: queryResult.hasNextPage && !queryResult.isFetchingNextPage,
    loading: queryResult.isFetchingNextPage,
    ...optionsRef.current.infiniteScrollOptions
  });

  /** 쿼리 결과와 lastElementRef를 반환합니다. */
  return {
    ...queryResult,
    dataObject,
    dataSource,
    totalElements: totalEl.totalElements,
    totalPages: totalEl.totalPages,
    lastElementRef
  };
};
